import React from "react";
import ReactDOM from "react-dom";
import { MsalProvider } from "@azure/msal-react";
import {
  AuthenticationResult,
  Configuration,
  EventMessage,
  EventType,
  IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser";
import axios from "axios";
import App from "./App";
import TesaTheme from "./styles/TesaTheme";
import * as serviceWorker from "./serviceWorker";
import { UserContextProvider } from "./contexts/UserContext";
import ConfirmationDialogContext, {
  ConfirmationDialogContextProvider,
} from "./components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ErrorSnackbarContext, { ErrorSnackbarContextProvider } from "./contexts/ErrorSnackbarContext";
import DashboardContextProvider from "./contexts/DashboardContext";
import { UploadContextProvider } from "./contexts/UploadContext";
import AttachmentFileUploadHandler from "./helper/AttachmentFileUploadHandler";
import AttachmentApi from "./api/services/AttachmentApi";
import NavigationContextProvider from "./contexts/NavigationContext";
import TestRequestProvider from "./contexts/TestRequestContext";
import Api from "./api/services/Api";
import PublicClientApplicationHolder from "./PublicClientApplicationHolder";
import { LaboratoriesProvider } from "./contexts/LaboratoriesContext";
import SpecialContentContextProvider from "./contexts/SpecialContentContext";

interface EnvObject {
  oidc_authority: string;
  oidc_clientId: string;
  oidc_redirectUri: string;
}

function loadEnvironment(): Promise<EnvObject> {
  return axios.get(`./static/env/env.json?t=${new Date().getTime()}`).then((res) => {
    const envObject = res.data;
    localStorage.setItem("envObject", JSON.stringify(envObject));
    return envObject;
  });
}

function setupSecurity(envObject: EnvObject): Promise<IPublicClientApplication> {
  const msalConfig: Configuration = {
    auth: {
      authority: envObject.oidc_authority,
      clientId: envObject.oidc_clientId,
      redirectUri: envObject.oidc_redirectUri,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
  };

  const publicClientApplication = new PublicClientApplication(msalConfig);

  const accounts = publicClientApplication.getAllAccounts();
  if (accounts.length > 0) {
    publicClientApplication.setActiveAccount(accounts[0]);
  }

  publicClientApplication.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const { account } = payload;
      publicClientApplication.setActiveAccount(account);
    }
  });

  return publicClientApplication
    .handleRedirectPromise()
    .then(() => {
      const scopes = ["openid"];
      if (!publicClientApplication.getActiveAccount()) {
        // If no account is present redirect anonymous user to login page
        return publicClientApplication.loginRedirect({ scopes });
      }
      // try to fetch token silently
      return (
        publicClientApplication
          .acquireTokenSilent({ scopes })
          .then(() => undefined)
          // on error fetching token silently redirect the user to login page
          .catch(() => publicClientApplication.loginRedirect({ scopes }))
      );
    })
    .then(() => PublicClientApplicationHolder.storeInstance(publicClientApplication));
}

function renderApp(pca: IPublicClientApplication) {
  ReactDOM.render(
    <MsalProvider instance={pca}>
      <ConfirmationDialogContextProvider>
        <ErrorSnackbarContextProvider>
          <UserContextProvider>
            <DashboardContextProvider>
              <SpecialContentContextProvider>
                <TesaTheme>
                  <ErrorSnackbarContext.Consumer>
                    {({ handleHttpError }) => {
                      return (
                        <NavigationContextProvider>
                          <TestRequestProvider>
                            <ConfirmationDialogContext.Consumer>
                              {(dialogContext) => (
                                <UploadContextProvider
                                  fileUploadHandler={
                                    new AttachmentFileUploadHandler(new AttachmentApi(new Api(dialogContext)))
                                  }
                                  onHttpError={handleHttpError}
                                >
                                  <LaboratoriesProvider>
                                    <App />
                                  </LaboratoriesProvider>
                                </UploadContextProvider>
                              )}
                            </ConfirmationDialogContext.Consumer>
                          </TestRequestProvider>
                        </NavigationContextProvider>
                      );
                    }}
                  </ErrorSnackbarContext.Consumer>
                </TesaTheme>
              </SpecialContentContextProvider>
            </DashboardContextProvider>
          </UserContextProvider>
        </ErrorSnackbarContextProvider>
      </ConfirmationDialogContextProvider>
    </MsalProvider>,
    document.getElementById("root"),
  );
}

// Point of entry
loadEnvironment()
  .then(setupSecurity)
  .then(renderApp)
  .catch((error) => {
    console.error(error);
  });

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
